'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Typography from '@/components/Typography'
import NextImage from 'next/image'
import styles from './styles.module.css'
import AnimatedSection from '@/components/AnimatedSection'
import Contact from '@/app/_blocks/Main_redesign/Contact'
import useEmblaCarousel from 'embla-carousel-react'
import { StrapiArticle, getStrapiImageUrl } from '@/lib/strapi'

const categories = ['Все', 'Промышленность', 'Финансы', 'Юридические услуги', 'Строительство', 'Ритейл', 'Другое']

type ArticlesContentProps = {
  initialArticles: StrapiArticle[]
}
export default function ArticlesContent({ initialArticles }: ArticlesContentProps) {
	const router = useRouter()
	const [strapiArticles] = useState<StrapiArticle[]>(initialArticles)
	const [activeCategory, setActiveCategory] = useState<string>('Все')
	const [visibleCount, setVisibleCount] = useState<number>(9)

	const categoriesWithArticles = [
		'Все',
		...categories
			.filter(category => category !== 'Все')
			.filter(category => strapiArticles.some(article => article.category === category))
	]

	const [emblaRef] = useEmblaCarousel({
		dragFree: true,
		align: 'start',
	  })
	
	  // Сбрасываем счетчик при изменении категории
	  useEffect(() => {
		setVisibleCount(9)
	  }, [activeCategory])
	
	  // Фильтрация по категориям из Strapi
	  const filteredArticles = activeCategory === 'Все'
		? strapiArticles 
		: strapiArticles.filter(article => article.category === activeCategory)

	const firstArticle = filteredArticles.length > 0 ? filteredArticles[0] : null
	const otherArticles = filteredArticles.length > 1 ? filteredArticles.slice(1) : []
	const visibleArticles = otherArticles.slice(0, visibleCount)
	const hasMore = otherArticles.length > visibleCount

	const handleLoadMore = () => {
		setVisibleCount(prev => prev + 3)
	}

	// Функция для обработки клика по карточке статьи
	const handleArticleClick = (article: StrapiArticle) => {
		if (article.isExternal && article.externalUrl) {
			window.open(article.externalUrl, '_blank', 'noopener,noreferrer')
		} else {
			router.push(`/articles/${article.slug}`)
		}
	}

	return (
		<>
			<AnimatedSection animation="slideUp">
				<div className={styles.main__container}>
					<div className={styles.main__title}>
						<Typography variant='h1'>Наше экспертное мнение</Typography>
					</div>
					<div className={styles.main__description}>
						<p>Здесь мы делимся своим экспертным мнением в области ИТ-разработки и цифровой трансформации бизнеса</p>
					</div>
				</div>
			</AnimatedSection>

			<AnimatedSection animation="slideUp">
				<div className={styles.categories}>
					<div className={styles.embla__viewport} ref={emblaRef}>
						<div className={styles.embla__container}>
						{categoriesWithArticles.map((category) => (
								<div key={category} className={styles.embla__slide}>
									<button
										className={`${styles.categoryButton} ${activeCategory === category ? styles.categoryButtonActive : ''}`}
										onClick={() => setActiveCategory(category)}
									>
										{category}
									</button>
								</div>
							))}
						</div>
					</div>
				</div>
			</AnimatedSection>

			<section>
				{firstArticle && (
				<AnimatedSection animation="slideUp">
					<div className={styles.card} data-idx={0}>
						<div 
							className={styles.card__image__container}
							onClick={() => handleArticleClick(firstArticle)}
						>
							<div className={styles.imageBackground}>
								<NextImage
									className={styles.card__image}
									src={firstArticle.featuredImage?.url ? 
										getStrapiImageUrl(firstArticle.featuredImage.url) : 
										'/images/blog/default.jpg'
									}
									alt={firstArticle.featuredImage?.alternativeText || firstArticle.title}
									fill
									sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
									placeholder='blur'
									blurDataURL='data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=='
									priority
								/>
							</div>
						</div>
						<div className={styles['card-content']}>
							<Typography variant='h4' className={styles.card__headline}>
								{firstArticle.title}
							</Typography>
							<Typography variant='p3' color='description' className={styles.card__description}>
								{firstArticle.shortDescription}
							</Typography>
							<div className={styles.link}>
								<div 
									className={styles.linkContent}
									onClick={() => handleArticleClick(firstArticle)}
								>
									<span className={styles.linkText}>Подробнее</span>
									<div className={styles.linkIcon}>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
											<path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="#f23b67" />
										</svg>
									</div>
								</div>
							</div>
							<div className={styles.card__footer}>
								{firstArticle.publishedDate && (
									<Typography variant='p2'>
										{new Date(firstArticle.publishedDate).toLocaleDateString('ru-RU', {
											day: '2-digit',
											month: '2-digit',
											year: 'numeric'
										})}
									</Typography>
								)}
							</div>
						</div>
					</div>
				</AnimatedSection>
				)}

				{visibleArticles.length > 0 && (
				<div className={styles.blogs}>
					{visibleArticles.map((article, idx) => (
						<AnimatedSection key={article.id} animation="slideUp">
							<div className={styles.card} data-idx={idx + 1}>
								<div 
									className={styles.card__image__container}
									onClick={() => handleArticleClick(article)}
								>
									<div className={styles.imageBackground}>
										<NextImage
											className={styles.card__image}
											src={article.featuredImage?.url ? 
												getStrapiImageUrl(article.featuredImage.url) : 
												'/images/blog/default.jpg'
											}
											alt={article.featuredImage?.alternativeText || article.title}
											fill
											sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
											placeholder='blur'
											blurDataURL='data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=='
											priority
										/>
									</div>
								</div>
								<div className={styles['card-content']}>
									<Typography variant='h4' className={styles.card__headline}>
										{article.title}
									</Typography>
									<Typography variant='p3' color='description' className={styles.card__description}>
										{article.shortDescription}
									</Typography>
									<div className={styles.link}>
										<div 
											className={styles.linkContent}
											onClick={() => handleArticleClick(article)}
										>
											<span className={styles.linkText}>Подробнее</span>
											<div className={styles.linkIcon}>
												<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
													<path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="#f23b67" />
												</svg>
											</div>
										</div>
									</div>
									<div className={styles.card__footer}></div>
								</div>
							</div>
						</AnimatedSection>
					))}
				</div>
				)}

				{hasMore && (
					<AnimatedSection animation="slideUp">
						<button className={styles.loadMoreButton} onClick={handleLoadMore}>
							<span className={styles.loadMoreText}>Показать ещё</span>
							<svg className={styles.loadMoreIcon} width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M0.94 0L4 3.05333L7.06 0L8 0.94L4 4.94L0 0.94L0.94 0Z" fill="white"/>
							</svg>
						</button>
					</AnimatedSection>
				)}
			</section>

			<AnimatedSection animation="fadeIn">
				<div className={styles.contactBlock}>
					<Contact />
				</div>
			</AnimatedSection>
		</>
	)
}
